import { Alert, Button, Input, Select, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import AssignDeviceIcon from "../../../assets/icons/AssignDeviceIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import GoAlertFill from "../../../assets/icons/GoAlertFill";
import IoIosSendIcon from "../../../assets/icons/IoIosSendIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import UploadIcon from "../../../assets/icons/UploadIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import TopCard from "../../../components/common/TopCard";
import AddEmployeeModal from "../../../components/Employees/AddEmployeeModal";
import BKashNumberModal from "../../../components/Employees/BKashNumberModal";
import CreateDeviceUserModal from "../../../components/Employees/CreateDeviceUserModal";
import GrossSalaryModal from "../../../components/Employees/GrossSalaryModal";
import InviteEmployeeModal from "../../../components/Employees/InviteEmployeeModal";
import StartEnrollmentModal from "../../../components/Employees/StartEnrollmentModal";
import UserInfoModal from "../../../components/Employees/UserInfoModal";
import VariableSalaryModal from "../../../components/Employees/VariableSalaryModal";
import RequestDateWithOutDateModal from "../../../components/Report/RequestDateWithOutDateModal";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import colors from "../../../constants/colors";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import {
  useGetEmployeesQuery,
  useReInvitedMutation,
  useUpdateEmployeeBulkStatusMutation,
} from "../../../features/employee/employeeApi";
import { useGetPayrollSettingQuery } from "../../../features/payrollSetting/payrollSettingApi";
import { useEmployeeReportMutation } from "../../../features/report/reportApi";
import { useGetWorkStationsQuery } from "../../../features/workStation/workStationApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { EmployeeData, GetEmployeeParamsType } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { replaceAllWithUndefined } from "../../../utils/obj";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOptionWithAll,
  selectSearchOption,
  selectStringOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

enum Status {
  active = "status-success",
  inactive = "status-danger",
  invited = "status-info",
}

type ModalType =
  | "INVITE"
  | "GROSS_SALARY"
  | "VARIABLE_SALARY"
  | "BKASH_NUMBER"
  | "BULK_ACTION"
  | "SEND_INVITATION_AGAIN"
  | "START_ENROLLMENT"
  | "CREATE_DEVICE_USER"
  | "REPORT_REQUEST"
  | "USER_INFO";

const searchTypeSelect = ["name", "email", "employee_id", "mobile"]?.map(
  (item) => ({
    label: underscoreToCapitalizeString(item),
    value: item,
  })
);

type GetEmployeeTypeParams = {
  attendanceType?: "remote" | "location" | "wifi";
} & GetEmployeeParamsType;

export default function Employees() {
  const location = useLocation();
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { open, handleClose, handleOpen } = useModal<string, ModalType>();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { query, setQuery } = useQueryParams();

  const getNumberFromQuery = (value?: string) => {
    if (!value) return undefined;

    const parsedValue = Number(value);
    return isNaN(parsedValue) ? undefined : parsedValue;
  };

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleFilter,
    filter,
    keyword,
    handleSearch,
    inputValue,
  } = useTable<GetEmployeeTypeParams>({
    defaultKeyword: query.search || "",
    defaultFilter: {
      search_type:
        (query.search_type as GetEmployeeTypeParams["search_type"]) || "name",
      status: (query.status as GetEmployeeTypeParams["status"]) || "active",
      gender: (query.gender as GetEmployeeTypeParams["gender"]) || undefined,
      employee_type:
        (query.employee_type as GetEmployeeTypeParams["employee_type"]) ||
        undefined,
      dashboard:
        (query.dashboard as GetEmployeeTypeParams["dashboard"]) || undefined,
      business_department_id: (getNumberFromQuery(
        query.business_department_id
      ) || undefined) as GetEmployeeTypeParams["business_department_id"],
      business_role_id: (getNumberFromQuery(
        query.business_role_id
      ) || undefined) as GetEmployeeTypeParams["business_role_id"],
      business_work_station_id: getNumberFromQuery(query.business_work_station_id),
      attendanceType:
        (query.attendanceType as GetEmployeeTypeParams["attendanceType"]) ||
        undefined,
      is_in_attendance_device:
        query.is_in_attendance_device === "true"
          ? true
          : query.is_in_attendance_device === "false"
          ? false
          : undefined,
    },
  });
  const [
    updateEmployeeBulkStatus,
    {
      isLoading: isBulkStatusLoading,
      isError: isBulkStatusError,
      error: bulkStatusError,
      isSuccess: isBulkStatusSuccess,
    },
  ] = useUpdateEmployeeBulkStatusMutation();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isError: isWorkStationError,
    error: workStationError,
    data: workStationData,
  } = useGetWorkStationsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(filter?.business_department_id
          ? {
              business_department_id:
                filter?.business_department_id?.toString() || "",
            }
          : {}),
        is_published: 1,
      },
    },
    {
      skip: !filter?.business_department_id,
    }
  );

  const employeeParams = {
    ...replaceAllWithUndefined(filter),
    search_type: undefined,
    ...(keyword ? { search_type: filter.search_type } : {}),
    search: keyword,
    is_location_active: filter?.attendanceType === "location" ? 1 : undefined,
    is_remote_active: filter?.attendanceType === "remote" ? 1 : undefined,
    is_wifi_network_active: filter?.attendanceType === "wifi" ? 1 : undefined,
    attendanceType: undefined,
  } as GetEmployeeTypeParams;

  useEffect(() => {
    const params = new URLSearchParams();
    const {
      limit: _limit,
      skip: _skip,
      ...filterWithoutPagination
    } = filter as GetEmployeeTypeParams & { limit?: number; skip?: number };

    Object.entries(filterWithoutPagination).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });

    if (keyword) {
      params.set("search", keyword);
    }

    const nextQuery = params.toString();
    const currentQuery = location.search.replace("?", "");

    if (nextQuery !== currentQuery) {
      setQuery(params, { replace: true });
    }
  }, [filter, keyword, location.search, setQuery]);

  const { isLoading, isError, error, data } = useGetEmployeesQuery({
    params: employeeParams,
  });
  const [
    reInvited,
    {
      isError: isReInviteError,
      error: reInviteError,
      isSuccess: isReInviteSuccess,
      isLoading: isReInviteLoading,
    },
  ] = useReInvitedMutation();
  const {
    data: payrollSettingData,
    isError: isPayrollSettingError,
    error: payrollSettingError,
  } = useGetPayrollSettingQuery();
  const [
    employeeReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useEmployeeReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isBulkStatusError, bulkStatusError, "Update bulk status fail");
  useError(isReInviteError, reInviteError, "Re-invitation mail sent fail");
  useError(
    isPayrollSettingError,
    payrollSettingError,
    "Gross salary configured data fail "
  );
  useError(isWorkStationError, workStationError, "Work station get fail");

  const isShowModal =
    payrollSettingData?.data?.payroll_setting?.pay_components?.pay_schedule
      ?.is_enable;

  const handleUpdateStatus = () => {
    updateEmployeeBulkStatus({
      status: open?.data,
      employee_id: selectedData,
    });
  };

  const handleSandInvitationAgain = () => {
    const { id, ...rest } = JSON.parse(open?.data || "");

    reInvited({
      id,
      body: {
        ...rest,
      },
    });
  };

  useEffect(() => {
    if (isBulkStatusSuccess) {
      toast("success", "Statuses updated successfully");
      setSelectedData([]);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkStatusSuccess]);

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee Id",
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val, record) => (
        <span className="flex items-center gap-1.5">
          {record?.payroll_show_alert && isShowModal ? (
            <Tooltip placement="top" title="Salary not configured!">
              <GoAlertFill
                style={{
                  color: colors?.danger?.color,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          ) : null}{" "}
          {val ?? "n/a"}
        </span>
      ),
    },
    {
      title: "Employee Name",
      width: 310,
      dataIndex: "member",
      sorter: (a, b) =>
        a?.member?.profile?.name?.localeCompare(b?.member?.profile?.name),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.profile?.name ?? "n/a",
    },
    {
      title: "Email",
      dataIndex: "member",
      render: (val) => val?.profile?.email ?? "n/a",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Department",
      dataIndex: "role",
      render: (val) => val?.department?.name ?? "n/a",
    },
    {
      title: "Attendance Type",
      // align: "center",
      render: (val) => {
        const isEmpty =
          !val?.is_wifi_network_active &&
          !val?.is_remote_active &&
          !val?.is_location_active;

        return (
          <div className="inline-flex items-center gap-1 min-w-[118px]">
            {isEmpty ? "-" : null}
            {val?.is_wifi_network_active ? (
              <span className="tooltip-box !cursor-default">W</span>
            ) : null}{" "}
            {val?.is_remote_active ? (
              <span className="tooltip-box !cursor-default">H</span>
            ) : null}
            {val?.is_location_active ? (
              <span className="tooltip-box !cursor-default">G</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status, record) => (
        <div className="flex items-center gap-2">
          <span className={Status[val]}>{capitalize(val)}</span>

          {val === "invited" && (
            <Tooltip placement="top" title="Send invitation again">
              <button
                className="text-[#001652]"
                onClick={() =>
                  handleOpen({
                    type: "SEND_INVITATION_AGAIN",
                    data: JSON.stringify({
                      id: record?.id,
                      email: record?.member?.profile?.email,
                      profile_id: record?.member?.profile?.id,
                    }),
                  })
                }
              >
                <IoIosSendIcon />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "In Device",
      dataIndex: "is_in_attendance_device",
      render: (val) => (val === true ? "Yes" : val === false ? "No" : "n/a"),
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",

      render: (val, record) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            className={`action-button bg-[#ffffff] `}
            size="small"
            icon={<AssignDeviceIcon />}
            disabled={record?.is_in_attendance_device === false}
            onClick={() =>
              handleOpen({
                type: "START_ENROLLMENT",
                data: val,
              })
            }
          />

          <Link to={`${location.pathname}/view/${val}${location.search}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (isReInviteSuccess) {
      toast("success", "Re-invitation mail sent successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReInviteSuccess]);

  return (
    <>
      <SidebarLayout title="Employees List">
        <TopCard
          className="mb-4"
          leftChildren={
            authInfo?.role === "Admin" && (
              <>
                {/* <Button
                  icon={<AddIcon />}
                  type="primary"
                  className="sm-button !px-3"
                  onClick={() =>
                    handleOpen({
                      type: "ADD",
                    })
                  }
                >
                  Add
                </Button> */}
                <Button
                  icon={<AddIcon />}
                  type="primary"
                  className="sm-button !px-3"
                  onClick={() =>
                    handleOpen({
                      type: "CREATE_DEVICE_USER",
                    })
                  }
                >
                  Create Device User
                </Button>
                {/* <Button
                  icon={<SentIcon />}
                  type="primary"
                  className="!bg-[#001652] sm-button !px-3"
                  onClick={() =>
                    handleOpen({
                      type: "INVITE",
                    })
                  }
                >
                  Invite
                </Button> */}
              </>
            )
          }
        >
          {authInfo?.role === "Admin" && (
            <>
              <Button
                className="upload-button"
                icon={<UploadIcon />}
                onClick={() =>
                  handleOpen({
                    type: "USER_INFO",
                  })
                }
              >
                Upload User Info
              </Button>
              <Button
                className="upload-button"
                icon={<UploadIcon />}
                onClick={() =>
                  handleOpen({
                    type: "VARIABLE_SALARY",
                  })
                }
              >
                Upload Variable Salary
              </Button>
              <Button
                className="upload-button"
                icon={<UploadIcon />}
                onClick={() =>
                  handleOpen({
                    type: "GROSS_SALARY",
                  })
                }
              >
                Upload Gross Salary
              </Button>
              <Button
                className="bkash-button"
                icon={<UploadIcon />}
                onClick={() =>
                  handleOpen({
                    type: "BKASH_NUMBER",
                  })
                }
              >
                Upload bKash A/C
              </Button>
            </>
          )}

          <Button
            onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
            className="download-button"
            icon={<DownloadIcon />}
          >
            Report Requests
          </Button>
        </TopCard>

        {isShowModal &&
        (filter.status === "active" || !filter.status) &&
        data?.data?.employees?.find(
          (item) => item?.status === "active" && item?.payroll_show_alert
        ) ? (
          <Alert
            message="One or more employees have not their salary configured. If not, then their salary will not be disbursed."
            type="warning"
            showIcon
            closable
            icon={<GoAlertFill style={{ color: "#fff", fontSize: 20 }} />}
            closeIcon={<CloseIcon width={10} height={10} />}
            className="mb-4 rounded bg-[#f59e0b] font-medium text-sm text-white !px-4 !py-2.5"
          />
        ) : null}

        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.employees)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowClassName={(record) =>
            record?.payroll_show_alert && isShowModal ? "alert_bg" : ""
          }
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as string[]);
            },
            getCheckboxProps: (record) => {
              return {
                disabled: record?.status === "invited",
              };
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              {selectedData?.length ? (
                <Select
                  showSearch
                  placeholder="Bulk Action"
                  // value={bulkAction}
                  options={bulkActionList}
                  onChange={(val) =>
                    handleOpen({
                      type: "BULK_ACTION",
                      data: val,
                    })
                  }
                  filterOption={selectSearchOption}
                  className="min-w-[100px]"
                  suffixIcon={<SelectDownIcon />}
                  rootClassName="icon-gray"
                />
              ) : null}
              <Select
                showSearch
                placeholder="Search Type"
                value={filter.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                popupMatchSelectWidth={false}
              />
              <Input
                key={filter.search_type}
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter.search_type || "",
                  true
                )}`}
                value={inputValue}
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={(val) => handleSearch(val)}
              />
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.business_department_id}
                onChange={(val) => {
                  handleFilter("business_department_id", val);
                  handleFilter("business_role_id", undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Designation"
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  designationData?.data?.roles || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.business_role_id}
                onChange={(val) => handleFilter("business_role_id", val)}
                disabled={!filter.business_department_id}
              />
              <Select
                allowClear
                showSearch
                placeholder="Work Station"
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  workStationData?.data?.workStations || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.business_work_station_id}
                onChange={(val) => {
                  handleFilter("business_work_station_id", val);
                }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Attendance Type"
                options={attendanceTypeList}
                value={filter.attendanceType}
                onChange={(val) => handleFilter("attendanceType", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Select
                allowClear
                showSearch
                placeholder="In Device"
                options={inDeviceList}
                value={
                  filter.is_in_attendance_device === true
                    ? "true"
                    : filter.is_in_attendance_device === false
                    ? "false"
                    : undefined
                }
                onChange={(val) =>
                  handleFilter(
                    "is_in_attendance_device",
                    val === "true" ? true : val === "false" ? false : undefined
                  )
                }
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  ["all", ...Object.keys(Status)],
                  "string",
                  true
                )}
                className="min-w-[90px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.status}
                onChange={(val) => handleFilter("status", val)}
              />
            </>
          }
        />

        {open.type === "ADD" && (
          <AddEmployeeModal
            open={open.open}
            handleClose={handleClose}
            showGrossAmount={!!isShowModal}
          />
        )}
        {open.type === "INVITE" && (
          <InviteEmployeeModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "GROSS_SALARY" && (
          <GrossSalaryModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "VARIABLE_SALARY" && (
          <VariableSalaryModal
            open={open.open}
            handleClose={handleClose}
            forEmployee
          />
        )}
        {open.type === "USER_INFO" && (
          <UserInfoModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "BKASH_NUMBER" && (
          <BKashNumberModal
            open={open.open}
            handleClose={handleClose}
            employeeParams={filter}
          />
        )}
        {open.type === "BULK_ACTION" && (
          <ConfirmModal
            open={open?.open}
            handleClose={handleClose}
            handleOk={handleUpdateStatus}
            confirmTitle={`${capitalize(open.data || "Update Status")} Users`}
            questionText={`Are you sure to ${
              open?.data || "update status"
            } this users?`}
            okText="Confirm"
            okLoading={isBulkStatusLoading}
          />
        )}

        {open?.type === "SEND_INVITATION_AGAIN" && (
          <ConfirmModal
            open={open.open}
            handleClose={handleClose}
            handleOk={handleSandInvitationAgain}
            confirmTitle="Confirmation"
            questionText="Are you sure you want send invitation mail again?"
            okText="Send"
            okLoading={isReInviteLoading}
          />
        )}

        {open.type === "START_ENROLLMENT" && (
          <StartEnrollmentModal
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )}

        {open.type === "CREATE_DEVICE_USER" && (
          <CreateDeviceUserModal open={open.open} handleClose={handleClose} />
        )}

        {open.type === "REPORT_REQUEST" && (
          <RequestDateWithOutDateModal
            open={open.open}
            handleClose={handleClose}
            isLoading={isGenerateLoading}
            isSuccess={isGenerateSuccess}
            // detailsMessage={`You are requesting a Report Export range for ${dayjs(
            //   filter?.date,
            //   dbDateFormat
            // ).format("DD/MM/YYYY")}`}
            handleSubmit={() => {
              employeeReport({
                body: {
                  name: "Employee List",
                },
                params: {
                  ...filter,
                  skip: 0,
                  limit: 0,
                },
              });
            }}
          />
        )}

        {/* {open.type === "REPORT_REQUEST" && (
          <ReportRequestModal open={open.open} handleClose={handleClose} />
        )} */}
      </SidebarLayout>
    </>
  );
}

const bulkActionList = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];

export const attendanceTypeList = [
  {
    value: "all",
    label: "All",
  },
  {
    label: "Remote",
    value: "remote",
  },
  {
    label: "Location",
    value: "location",
  },
  {
    label: "Wifi",
    value: "wifi",
  },
];

const inDeviceList = [
  {
    label: "Yes",
    value: "true",
  },
  {
    label: "No",
    value: "false",
  },
];
